import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { cn } from '@/lib/cn';
import { sections, type Section } from '@/lib/sections';
import { useAuth } from '@/contexts/AuthContext';
import { staggerContainer, staggerItem } from '@/design/animations';

/**
 * Home page — section overview grid.
 * Shows the sections this account may open.
 */
export function HomePage() {
  const { canAccessSection } = useAuth();
  const visibleSections = sections.filter(
    (s) => s.id !== 'settings' && canAccessSection(s.id),
  );

  return (
    <div className="px-8 py-8 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900">
          Product Dashboard
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Analytics, flows, and insights for your product
        </p>
      </div>

      {/* Section Grid */}
      <motion.div
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
        variants={staggerContainer}
        initial="hidden"
        animate="visible"
      >
        {visibleSections.map((section) => (
          <SectionCard key={section.id} section={section} />
        ))}
      </motion.div>
    </div>
  );
}

function SectionCard({ section }: { section: Section }) {
  const navigate = useNavigate();
  const Icon = section.icon;

  return (
    <motion.div
      variants={staggerItem}
      onClick={() => section.enabled && navigate(section.route)}
      className={cn(
        'group relative bg-white rounded-xl border border-slate-200/80 p-5',
        'shadow-soft-xs transition-all duration-200',
        section.enabled
          ? 'cursor-pointer hover:shadow-soft hover:border-slate-300/80 hover:-translate-y-[1px] active:translate-y-0'
          : 'opacity-50 cursor-not-allowed',
      )}
    >
      {/* Icon */}
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center mb-3.5"
        style={{ backgroundColor: `${section.accent}12` }}
      >
        <Icon
          className="w-5 h-5"
          style={{ color: section.accent }}
        />
      </div>

      {/* Text */}
      <h3 className="text-sm font-semibold text-slate-900 mb-1">
        {section.name}
      </h3>
      <p className="text-xs text-slate-500 leading-relaxed">
        {section.description}
      </p>

      {/* Arrow indicator */}
      {section.enabled && (
        <ArrowRight
          className={cn(
            'absolute top-5 right-5 w-4 h-4 text-slate-300',
            'transition-all duration-200',
            'group-hover:text-slate-400 group-hover:translate-x-0.5',
          )}
        />
      )}

      {/* Coming soon badge */}
      {!section.enabled && (
        <span className="absolute top-4 right-4 text-2xs font-medium text-slate-400 bg-slate-100 px-2 py-0.5 rounded-md">
          Coming soon
        </span>
      )}
    </motion.div>
  );
}
